import * as THREE from 'three';
import { PostmanController } from './PostmanController';

export class PortalManager {
  private scene: THREE.Scene;
  private postmanController: PostmanController;
  private exitPortalBox: THREE.Box3 | null = null;
  private startPortalBox: THREE.Box3 | null = null;
  private startPortalGroup: THREE.Group | null = null;
  private exitPortalGroup: THREE.Group | null = null;
  private hasPortalParam: boolean;
  private selfUsername: string = '';
  private lastCheckedTime: number = 0;
  private cooldownTime: number = 1000; // Cooldown in milliseconds

  constructor(scene: THREE.Scene, postmanController: PostmanController, playerName: string) {
    this.scene = scene;
    this.postmanController = postmanController;
    this.selfUsername = playerName;
    
    // Check if portal parameter exists in URL
    this.hasPortalParam = new URLSearchParams(window.location.search).get('portal') !== null;
    
    if (this.hasPortalParam) {
      this.createStartPortal();
    }
    
    console.log('PortalManager initialized with player name:', playerName);
    console.log('Portal param exists:', this.hasPortalParam);
  }

  private createStartPortal(): void {
    // Get the player spawn position
    const playerPosition = this.postmanController.getPosition();
    const SPAWN_POINT_X = playerPosition.x;
    const SPAWN_POINT_Y = 2; // Set to same height as exit portal (2 units above ground)
    const SPAWN_POINT_Z = playerPosition.z;

    // Create portal group to contain all portal elements
    this.startPortalGroup = new THREE.Group();
    this.startPortalGroup.position.set(SPAWN_POINT_X, SPAWN_POINT_Y, SPAWN_POINT_Z);
    // Update rotation to match the exit portal (horizontal orientation)
    this.startPortalGroup.rotation.x = 1.57; // Make portal horizontal instead of crooked (match exit portal)
    this.startPortalGroup.rotation.y = 0;
    // Add scaling to match the exit portal
    this.startPortalGroup.scale.set(0.1, 0.1, 0.1);

    // Create portal effect
    const startPortalGeometry = new THREE.TorusGeometry(15, 2, 16, 100);
    const startPortalMaterial = new THREE.MeshPhongMaterial({
        color: 0xff0000,
        emissive: 0xff0000,
        transparent: true,
        opacity: 0.8
    });
    const startPortal = new THREE.Mesh(startPortalGeometry, startPortalMaterial);
    // Add rotation to torus to match exit portal
    startPortal.rotation.x = Math.PI / 2;
    this.startPortalGroup.add(startPortal);
                        
    // Create portal inner surface
    const startPortalInnerGeometry = new THREE.CircleGeometry(13, 32);
    const startPortalInnerMaterial = new THREE.MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide
    });
    const startPortalInner = new THREE.Mesh(startPortalInnerGeometry, startPortalInnerMaterial);
    // Add rotation to match exit portal
    startPortalInner.rotation.x = Math.PI / 2;
    this.startPortalGroup.add(startPortalInner);

    // Add a larger collision area for the portal while keeping it invisible
    const collisionBox = new THREE.Mesh(
      new THREE.BoxGeometry(5, 5, 5),
      new THREE.MeshBasicMaterial({
        color: 0xff0000,
        transparent: true,
        opacity: 0.0 // Invisible but used for collision
      })
    );
    collisionBox.position.copy(this.startPortalGroup.position);
    this.scene.add(collisionBox);

    // Add portal group to scene
    this.scene.add(this.startPortalGroup);

    // Create portal collision box - use the larger collision box
    this.startPortalBox = new THREE.Box3().setFromObject(collisionBox);
  }

  public setExitPortalBox(portalBox: THREE.Box3): void {
    // Only log if the box is new or different
    if (!this.exitPortalBox || !this.exitPortalBox.equals(portalBox)) {
      console.log('Exit portal box set');
    }
    this.exitPortalBox = portalBox;
  }
  
  public setExitPortalGroup(portalGroup: THREE.Group): void {
    // Only log if the group is new or different
    if (!this.exitPortalGroup || this.exitPortalGroup.id !== portalGroup.id) {
      console.log('Exit portal group set');
    }
    this.exitPortalGroup = portalGroup;
  }

  public update(): void {
    // Throttle checks to avoid performance issues
    const currentTime = Date.now();
    if (currentTime - this.lastCheckedTime < this.cooldownTime) {
      return;
    }
    this.lastCheckedTime = currentTime;
    
    // Check for portal collisions
    if (this.hasPortalParam && this.startPortalBox) {
      // Check if player has entered start portal
      const player = this.postmanController.getPlayer();
      if (player) {
        const playerBox = new THREE.Box3().setFromObject(player);
        const portalDistance = playerBox.getCenter(new THREE.Vector3())
          .distanceTo(this.startPortalBox!.getCenter(new THREE.Vector3()));
        
        // Store the previous distance state to avoid repetitive logging
        const wasNearStartPortal = player.userData.wasNearStartPortal || false;
        const wasWithinStartPortal = player.userData.wasWithinStartPortal || false;
        
        // Only log when first entering the near-portal zone
        if (portalDistance < 5 && !wasNearStartPortal) {
          console.log('Player entered start portal proximity, distance:', portalDistance.toFixed(2));
          player.userData.wasNearStartPortal = true;
        } else if (portalDistance >= 5 && wasNearStartPortal) {
          player.userData.wasNearStartPortal = false;
        }
        
        if (portalDistance < 3) {
          // Only log when first entering the activation zone
          if (!wasWithinStartPortal) {
            console.log('Player within start portal threshold distance:', portalDistance.toFixed(2));
            player.userData.wasWithinStartPortal = true;
          }
          
          // Get ref from URL params
          const urlParams = new URLSearchParams(window.location.search);
          const refUrl = urlParams.get('ref');
          if (refUrl) {
            // Only redirect once actually in the portal - use intersection check plus small distance
            if (playerBox.intersectsBox(this.startPortalBox) || portalDistance < 2) {
              console.log('Player entered start portal, navigating to:', refUrl);
              // Add https if not present and include query params
              let url = refUrl;
              if (!url.startsWith('http://') && !url.startsWith('https://')) {
                url = 'https://' + url;
              }
              const currentParams = new URLSearchParams(window.location.search);
              const newParams = new URLSearchParams();
              for (const [key, value] of currentParams) {
                if (key !== 'ref') { // Skip ref param since it's in the base URL
                  newParams.append(key, value);
                }
              }
              const paramString = newParams.toString();
              window.location.href = url + (paramString ? '?' + paramString : '');
            }
          }
        } else if (portalDistance >= 3 && wasWithinStartPortal) {
          // Reset the within-portal state when moving away
          player.userData.wasWithinStartPortal = false;
        }
      }
    }

    // Check if player has entered exit portal
    if (this.exitPortalBox) {
      const player = this.postmanController.getPlayer();
      if (player) {
        const playerBox = new THREE.Box3().setFromObject(player);
        // Check if player is within 3 units of the portal
        const portalDistance = playerBox.getCenter(new THREE.Vector3())
          .distanceTo(this.exitPortalBox.getCenter(new THREE.Vector3()));
        
        // Store the previous distance state to avoid repetitive logging
        const wasNearPortal = player.userData.wasNearPortal || false;
        const wasWithinPortal = player.userData.wasWithinPortal || false;
        
        // Only log when first entering the near-portal zone
        if (portalDistance < 5 && !wasNearPortal) {
          console.log('Player entered portal proximity, distance:', portalDistance.toFixed(2));
          player.userData.wasNearPortal = true;
        } else if (portalDistance >= 5 && wasNearPortal) {
          player.userData.wasNearPortal = false;
        }
        
        if (portalDistance < 3) {
          // Only log when first entering the activation zone
          if (!wasWithinPortal) {
            console.log('Player within portal threshold distance:', portalDistance.toFixed(2));
            player.userData.wasWithinPortal = true;
          }
          
          // Start loading the next page in the background
          const currentParams = new URLSearchParams(window.location.search);
          const newParams = new URLSearchParams();
          newParams.append('portal', 'true');
          newParams.append('username', this.selfUsername);
          newParams.append('color', 'white');
          newParams.append('speed', '5'); // Assuming default speed is 5
          
          for (const [key, value] of currentParams) {
            if (!['portal', 'username', 'color', 'speed'].includes(key)) {
              newParams.append(key, value);
            }
          }
          
          const paramString = newParams.toString();
          const nextPage = 'https://portal.pieter.com' + (paramString ? '?' + paramString : '');
          
          // Only log once when preloading
          if (!document.getElementById('preloadFrame')) {
            console.log('Creating preload iframe for:', nextPage);
            const iframe = document.createElement('iframe');
            iframe.id = 'preloadFrame';
            iframe.style.display = 'none';
            iframe.src = nextPage;
            document.body.appendChild(iframe);
          }
          
          // Only redirect once actually in the portal - use intersection check plus small distance
          if (playerBox.intersectsBox(this.exitPortalBox) || portalDistance < 2) {
            console.log('Player in portal, navigating to:', nextPage);
            // Force navigate to the next page
            try {
              window.location.href = nextPage;
              // Alternative approach if the above doesn't trigger
              setTimeout(() => {
                if (window.location.href !== nextPage) {
                  console.log('Retry navigation');
                  window.location.replace(nextPage);
                }
              }, 500);
            } catch (e) {
              console.error('Navigation error:', e);
            }
          }
        } else if (portalDistance >= 3 && wasWithinPortal) {
          // Reset the within-portal state when moving away
          player.userData.wasWithinPortal = false;
        }
      }
    }
  }
} 